import { Grid, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { LinkExistingInovice } from "./LinkExistingInovice/LinkExistingInovice";
import { ClientInvoiceContainer, VatContainer } from "./styles";
import VatCountries from "./VatCountries/VatCountries";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { ClientInvoiceDTO, ClientInvoiceRef } from "../../../../models/legal-entity-clients/client-invoices";
import { TransactionDTO } from "../../../../models/transactions/transaction-dto";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";

import { CountriesApiService } from "../../../../services/countries/countries-api.service";

import TransactionContext from "../../transaction-context/transaction-context";

const defaultClientInvoice: ClientInvoiceRef = {
  id: uuidv4(),
  companyAccount: null,
  isDeleted: false,
  client: null,
  invoiceNumber: "",
  totalAmount: 0,
  comment: "",
  vatNumber: "",
  currency: null,
  issueDate: new Date(),
  dueDate: new Date(),
  vatCountry: null,
  vatBases: [],
  sentToClientOn: null,
  sentToAccountingOn: null,
  paidOn: null,
};

interface Props {
  transaction: TransactionDTO;
  handleChange: (field: keyof TransactionDTO, value: TransactionDTO[keyof TransactionDTO]) => void;
  defaultTransaction?: TransactionDTO | null;
}

const IssuedClientInvoice = ({
  transaction: { clientInvoice, client, amount, currency, comment, transactionDate },
  transaction,
  handleChange,
  defaultTransaction,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_transaction.transaction_screen_two.client_invoice"; // tRP - translation root path

  const context = useContext(TransactionContext);

  useEffect(() => {
    if (client || amount || currency || comment || transactionDate) {
      handleChange("clientInvoice", {
        ...defaultClientInvoice,
        id: uuidv4(),
        ...clientInvoice,
        ...(context?.invoice ? context.invoice : {}),
        client,
        currency,
        totalAmount: amount,
        comment,
        issueDate: CustomDate.ToDate(transactionDate) as Date,
      });
    }
  }, [client, amount, currency, comment, transactionDate]);

  const handleChangeClientInvoice = (
    field: keyof ClientInvoiceDTO,
    value: ClientInvoiceDTO[keyof ClientInvoiceDTO]
  ) => {
    handleChange("clientInvoice", clientInvoice ? { ...clientInvoice, [field]: value } : null);
  };

  return (
    <Grid sx={{ display: "flex", flexDirection: "row", gap: "5px" }}>
      <ClientInvoiceContainer>
        {/* <AddClient buttonVariant="contained" buttonText="text" /> */}
        <TextField
          label={`${t(`${tRP}.invoice_number`)}`}
          variant="outlined"
          value={clientInvoice?.invoiceNumber ?? ""}
          onChange={(e) => handleChangeClientInvoice("invoiceNumber", e.target.value)}
          sx={{ maxWidth: "300px" }}
        />
        <DesktopDatePicker
          value={clientInvoice?.dueDate}
          onChange={(newValue) => {
            handleChangeClientInvoice("dueDate", newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ maxWidth: "300px" }}
              label={`${t(`${tRP}.invoice_due_date`)}`}
            />
          )}
        />
        <VatContainer>
          <AutocompleteDropdown
            labelKey={t(`${tRP}.vat_country`)}
            field="vatCountry"
            serviceApi={new CountriesApiService()}
            handleChange={handleChangeClientInvoice}
            propsItem={clientInvoice}
            sxProps={{ maxWidth: "300px" }}
          />
          <VatCountries
            transaction={transaction}
            handleChange={handleChange}
          />
        </VatContainer>
      </ClientInvoiceContainer>
      {!context.invoice && !defaultTransaction?.clientInvoice && (
        <LinkExistingInovice
          client={client}
          invoiceType={"client"}
          handleNotifyParent={(invoice: any) => {
            handleChange("client", invoice?.client);
            handleChange("clientInvoice", invoice);
          }}
          item={transaction}
        />
      )}
    </Grid>
  );
};

export default IssuedClientInvoice;
